import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Container, Row } from 'reactstrap';

//Import Icons
import FeatherIcon from 'feather-icons-react';

// Import Images
import ImageMyWords from '../../assets/images/v3/my_words.png';
import ImageDiscover from '../../assets/images/v3/discover.png';
import ImageQuiz from '../../assets/images/v3/quiz.png';
import ImageChrome from '../../assets/images/chrome.png';
import ImageFirefox from '../../assets/images/firefox.png';
import ImageEdge from '../../assets/images/edge.png';
import ImageAppStore from '../../assets/images/app_store_badge_564x168.png';
import ImageGooglePlay from '../../assets/images/google_play_badge_trimmed_564x168.png';

import StoreBadges from '../../components/Shared/StoreBadges';
import { STORE_LINKS } from '../../utils/platform';

// Modal Video
import ModalVideo from 'react-modal-video';
import '../../../node_modules/react-modal-video/scss/modal-video.scss';

// Real entry from the HighVocab word store (same data the extension demo used),
// with three more everyday senses added.
const MILD = {
  word: 'mild',
  pronunciation: 'maɪld',
  translation: '穏やかな',
  cefr_level: 'A2',
  category: 'daily life',
  image_url: 'https://images.pexels.com/photos/6913391/pexels-photo-6913391.jpeg?auto=compress&cs=tinysrgb&w=700&h=300&fit=crop',
  audio_url: 'https://audio.convolines.co/hv_lexical_entries/3943.mp3',
  senses: [
    { part_of_speech: 'adjective', definition: 'not strong or severe; gentle or mild in degree', example: 'The doctor said it was just a mild cold.' },
    { part_of_speech: 'adjective', definition: 'not strong in flavor or spice; gentle taste', example: 'This curry has a mild, comforting flavor.' },
    { part_of_speech: 'adjective', definition: 'of weather: not very cold, or warmer than usual', example: 'We had a mild winter this year.' },
    { part_of_speech: 'adjective', definition: 'of a person: calm and gentle; not easily angered', example: 'He is a mild man who rarely raises his voice.' },
    { part_of_speech: 'adjective', definition: 'of a product: gentle and not harsh on the skin', example: 'Use a mild soap if you have sensitive skin.' },
  ],
  forms: ['milder', 'mildest', 'mildly', 'mildness'],
  similar: ['gentle', 'slight', 'soft', 'moderate', 'calm'],
  often_appears_as: ['It was just a mild headache.', 'The weather is mild for October.', 'I was mildly surprised.'],
};

const playAudio = () => {
  const audio = new Audio(MILD.audio_url);
  audio.play().catch(() => {});
};

// Swipeable definitions, like the app's Word Detail screen. Native scroll-snap,
// so it swipes on touch and scrolls with a trackpad; the dots jump to a slide.
const SenseCarousel = ({ senses }) => {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const onScroll = () => {
    const el = trackRef.current;
    const slide = el.firstElementChild;
    if (!slide) return;
    setActive(Math.round(el.scrollLeft / (slide.offsetWidth + 10)));
  };

  const goTo = (i) => {
    const el = trackRef.current;
    setActive(i);
    el.scrollTo({ left: el.children[i].offsetLeft - el.offsetLeft, behavior: 'smooth' });
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: 8 }}>
        <span className="word-detail-label">Definitions</span>
        <span className="word-detail-count">
          {active + 1} / {senses.length} · swipe
          <FeatherIcon icon="chevrons-right" style={{ width: 13, height: 13, marginLeft: 2, verticalAlign: '-2px' }} />
        </span>
      </div>
      <div className="sense-track" ref={trackRef} onScroll={onScroll}>
        {senses.map((sense, i) => (
          <div className="sense-slide" key={i}>
            <p className="sense-pos">
              {i + 1}. {sense.part_of_speech}
            </p>
            <p className="sense-def">{sense.definition}</p>
            <p className="sense-example">&ldquo;{sense.example}&rdquo;</p>
          </div>
        ))}
      </div>
      <div className="d-flex justify-content-center" style={{ gap: 6, marginTop: 10 }}>
        {senses.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Definition ${i + 1}`}
            onClick={() => goTo(i)}
            className={`sense-dot${i === active ? ' sense-dot--active' : ''}`}
          />
        ))}
      </div>
    </div>
  );
};

// A readable Word Detail screen for phones: everything the app shows for a word.
const AppPreview = () => (
  <div className="app-preview" aria-label="HighVocab word detail preview">
    <div className="app-preview-card word-detail">
      <img src={MILD.image_url} alt="Soft rose petals on white linen" className="word-detail-img" />
      <div className="word-detail-body">
        <div className="d-flex align-items-start justify-content-between">
          <div>
            <span className="word-detail-word">{MILD.word}</span>
            <div className="d-flex align-items-center" style={{ gap: 6, marginTop: 2 }}>
              <span className="app-preview-ipa">/{MILD.pronunciation}/</span>
              <button type="button" onClick={playAudio} aria-label="Play pronunciation" className="word-detail-audio">
                <FeatherIcon icon="volume-2" style={{ width: 16, height: 16 }} />
              </button>
            </div>
          </div>
          <div className="d-flex align-items-center" style={{ gap: 10, marginTop: 6 }}>
            <span className="word-detail-translation">{MILD.translation}</span>
            <FeatherIcon icon="bookmark" style={{ width: 22, height: 22, color: '#7c3aed' }} />
          </div>
        </div>
        <div className="d-flex" style={{ gap: 6, margin: '8px 0 16px' }}>
          <span className="app-preview-tag app-preview-tag--level">{MILD.cefr_level}</span>
          <span className="app-preview-tag">{MILD.category}</span>
        </div>

        <SenseCarousel senses={MILD.senses} />

        <div className="word-detail-section">
          <span className="word-detail-label">Word forms</span>
          <div className="d-flex flex-wrap" style={{ gap: 6 }}>
            {MILD.forms.map((f) => (
              <span key={f} className="word-chip">{f}</span>
            ))}
          </div>
        </div>

        <div className="word-detail-section">
          <span className="word-detail-label">Similar words</span>
          <div className="d-flex flex-wrap" style={{ gap: 6 }}>
            {MILD.similar.map((f) => (
              <span key={f} className="word-chip">{f}</span>
            ))}
          </div>
        </div>

        <div className="word-detail-section">
          <span className="word-detail-label">Commonly used</span>
          {MILD.often_appears_as.map((ex) => (
            <p key={ex} className="word-detail-phrase">{ex}</p>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const MobileHero = ({ platform }) => (
  <section className="bg-half-170 border-bottom agency-wrapper d-table w-100 hero-mobile" id="home">
    <Container>
      <div className="title-heading text-center mt-3">
        <h1 className="heading mb-3">
          Remember the English words <span className="text-primary">you learn.</span>
        </h1>
        <p className="text-muted mb-4">
          Discover useful words, save your favorites, and practice with quizzes. Start free on your phone.
        </p>
        <StoreBadges platform={platform} height={54} />
        <p className="hero-mobile-meta mt-3 mb-2">Free to start · 50,000+ English words</p>
        <a href="#extension" className="small fw-semibold">
          Explore the desktop extension ↓
        </a>
      </div>
      <AppPreview />
    </Container>
  </section>
);

const DesktopHero = () => {
  const [open, setOpen] = useState(false);
  const [videoId, setVideoId] = useState('');

  const openModal = (id) => {
    setVideoId(id);
    setOpen(true);
  };

  return (
    <section
      className="bg-half-170 border-bottom agency-wrapper d-table w-100"
      id="home"
    >
      <Container>
        <Row className="align-items-center">
          <Col lg={7} md={7} className="order-1 order-md-2">
            <div className="title-heading mt-4 ms-lg-5">
              <h1 className="heading mb-3">
                Learn English Vocabulary{' '}
                <span className="text-primary">While You Browse</span>
              </h1>
              <p className="para-desc text-muted">
                Save new words from any website and see them highlighted
                whenever they appear again.
              </p>
              {/* Stats strip */}
              <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px 18px', marginTop: 4 }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>50,000+ words</span>
                <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#cbd5e1' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>60+ languages</span>
                <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#cbd5e1' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Free to start</span>
              </div>
              {/* Primary CTA */}
              <div className="mt-4">
                <a
                  href={STORE_LINKS.chrome}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-primary btn-lg d-inline-flex align-items-center"
                  style={{ gap: 10 }}
                >
                  <img src={ImageChrome} style={{ width: 22, height: 22 }} alt="" />
                  Install on Chrome — it&apos;s free
                </a>
              </div>

              {/* Secondary install options */}
              <div className="d-flex align-items-center flex-wrap gap-3 mt-3">
                <a href={STORE_LINKS.edge} target="_blank" rel="noreferrer" className="d-inline-flex align-items-center text-muted small" style={{ gap: 6 }}>
                  <img src={ImageEdge} style={{ width: 16, height: 16 }} alt="" />
                  Edge
                </a>
                <a href={STORE_LINKS.firefox} target="_blank" rel="noreferrer" className="d-inline-flex align-items-center text-muted small" style={{ gap: 6 }}>
                  <img src={ImageFirefox} style={{ width: 16, height: 16 }} alt="" />
                  Firefox
                </a>
                <span style={{ width: 1, height: 14, background: '#e2e8f0' }} />
                <a href={STORE_LINKS.ios} target="_blank" rel="noreferrer" style={{ display: 'inline-block', lineHeight: 0, opacity: 0.85 }}>
                  <img src={ImageAppStore} alt="Download on the App Store" style={{ height: 30, width: 'auto' }} />
                </a>
                <a href={STORE_LINKS.android} target="_blank" rel="noreferrer" style={{ display: 'inline-block', lineHeight: 0, opacity: 0.85 }}>
                  <img src={ImageGooglePlay} alt="Get it on Google Play" style={{ height: 30, width: 'auto' }} />
                </a>
              </div>

              {/* Video links */}
              <div className="d-flex flex-wrap gap-3 mt-4">
                <div className="d-flex align-items-center gap-2">
                  <Link to="#" onClick={() => openModal('ccO9w6J8MeE')} className="btn btn-icon btn-pills btn-primary lightbox">
                    <FeatherIcon icon="video" className="icons" />
                  </Link>
                  <span className="fw-bold text-uppercase small">How to use</span>
                </div>
              </div>
            </div>

            <ModalVideo
              channel="youtube"
              isOpen={open}
              videoId={videoId}
              onClose={() => setOpen(false)}
            />
          </Col>
          <Col
            lg={5}
            md={5}
            className="order-2 order-md-1 mt-4 pt-2 mt-sm-0 pt-sm-0 d-none d-md-flex align-items-center justify-content-center"
          >
            <div style={{ position: 'relative', width: 300, height: 420 }}>
              {/* Back-left: Discover */}
              <div style={{
                position: 'absolute',
                left: 0,
                top: 30,
                width: 160,
                transform: 'rotate(-6deg)',
                borderRadius: 20,
                overflow: 'hidden',
                boxShadow: '0 16px 40px rgba(15,23,42,0.14)',
                border: '1px solid rgba(15,23,42,0.06)',
                zIndex: 1,
              }}>
                <img src={ImageDiscover} alt="Discover words" style={{ width: '100%', display: 'block' }} />
              </div>

              {/* Back-right: Quiz */}
              <div style={{
                position: 'absolute',
                right: 0,
                top: 50,
                width: 155,
                transform: 'rotate(5deg)',
                borderRadius: 20,
                overflow: 'hidden',
                boxShadow: '0 16px 40px rgba(15,23,42,0.12)',
                border: '1px solid rgba(15,23,42,0.06)',
                zIndex: 1,
              }}>
                <img src={ImageQuiz} alt="Quiz yourself" style={{ width: '100%', display: 'block' }} />
              </div>

              {/* Front-center: My Words */}
              <div style={{
                position: 'absolute',
                left: '50%',
                top: 0,
                transform: 'translateX(-50%)',
                width: 175,
                borderRadius: 22,
                overflow: 'hidden',
                boxShadow: '0 24px 60px rgba(15,23,42,0.2)',
                border: '1px solid rgba(15,23,42,0.07)',
                zIndex: 2,
              }}>
                <img src={ImageMyWords} alt="My word list" style={{ width: '100%', display: 'block' }} />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

const Section = ({ platform }) =>
  platform === 'desktop' ? <DesktopHero /> : <MobileHero platform={platform} />;

export default Section;
